import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import ControlPanel from './components/ControlPanel';
import StatisticsPanel from './components/StatisticsPanel';
import Login from './components/Login';
import LogoutButton from './components/LogoutButton';
import { useAuth } from './hooks/useAuth';
import './App.css';

// Verificar que las variables de entorno estén configuradas
if (!process.env.REACT_APP_CONTROL_PANEL_PASSWORD) {
  console.warn('⚠️  REACT_APP_CONTROL_PANEL_PASSWORD no está configurada en .env');
}

const API_BASE_URL = process.env.NODE_ENV !== 'development'
  ? 'https://ticketsmut.fortunachile.cl:5000'
  : 'http://localhost:5000';

const socketConfig = {
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  forceNew: true,
  withCredentials: true
};

function App() {
  const [currentTicket, setCurrentTicket] = useState(0);
  const [socket, setSocket] = useState(null);
  const [activeTab, setActiveTab] = useState('control');
  const [ticketLimits, setTicketLimits] = useState({ min: 0, max: 999 });
  const [connectionStatus, setConnectionStatus] = useState('Conectando...');

  const auth = useAuth();

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    // Obtener número actual al cargar
    axios.get(`${API_BASE_URL}/api/current`)
      .then(response => {
        setCurrentTicket(response.data.current_number);
        setTicketLimits({
          min: response.data.min_ticket,
          max: response.data.max_ticket
        });
        setConnectionStatus('Conectado');
      })
      .catch(error => {
        console.error('Error obteniendo número actual:', error);
        setConnectionStatus('Error de conexión');
      });

    // Configurar Socket.io
    const newSocket = io(API_BASE_URL, socketConfig);
    setSocket(newSocket);

    // Escuchar actualizaciones
    newSocket.on('ticketUpdated', (ticketNumber) => {
      setCurrentTicket(ticketNumber);
    });

    newSocket.on('limitsUpdated', (limits) => {
      setTicketLimits(limits);
    });

    newSocket.on('connect', () => {
      setConnectionStatus('Conectado');
    });

    newSocket.on('disconnect', () => {
      setConnectionStatus('Desconectado');
    });

    return () => newSocket.close();
  }, [auth.isAuthenticated]);

  const changeTicket = (action) => {
    axios.post(`${API_BASE_URL}/api/update`, { action })
      .catch(error => {
        console.error('Error actualizando número:', error);
      });
  };

  if (auth.isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Login onLogin={() => window.location.reload()} auth={auth} />;
  }

  return (
    <div className="App">
      <div className="app-header">
        <h1>Panel de Control - Sistema de Tickets</h1>
        <div className="header-controls">
          <div className="connection-status">
            {connectionStatus}
          </div>
          <LogoutButton onLogout={auth.logout} />
        </div>
      </div>

      <div className="header-info">
        <div className="limits-info">
          Límites: {ticketLimits.min} - {ticketLimits.max}
        </div>
        <div className="session-info">
          Sesión activa • {Math.floor((parseInt(localStorage.getItem('loginTimestamp') || '0') + parseInt(process.env.REACT_APP_SESSION_DURATION_HOURS || '8') * 3600000 - Date.now()) / 3600000)}h restantes
        </div>
      </div>

      <div className="tab-container">
        <div
          className={`tab ${activeTab === 'control' ? 'active' : ''}`}
          onClick={() => setActiveTab('control')}
        >
          Control
        </div>
        <div
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          Estadísticas
        </div>
      </div>

      {activeTab === 'control' ? (
        <ControlPanel
          currentTicket={currentTicket}
          onChangeTicket={changeTicket}
          ticketLimits={ticketLimits}
        />
      ) : (
        <StatisticsPanel />
      )}
    </div>
  );
}

export default App;